import React from 'react';
import CurrentProfile from 'flow/components/ui-components/CurrentProfile';
import ProfilePicker from 'flow/components/ui-components/ProfilePicker';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectIsAdmin } from 'flow/selectors/own-extension-selectors';
import { selectCanEditProfilesOfUser } from 'flow/selectors/profile-selectors';
import { Contact, ContactFull } from 'flow/types/Contact';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { ActiveProfile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { extractName } from 'flow/utils';
import { getDefaultProfile } from 'flow/utils/profile-utils';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';

interface Props {
    contact: TypedMap<ContactFull> | TypedMap<Contact>;
    activeProfile: TypedMap<ActiveProfile>;
    extensionKey: ExtensionKey;
    title?: string;
    skipAdminCheck?: boolean;
}

const ProfileItem = ({
    contact,
    activeProfile,
    extensionKey,
    title,
    skipAdminCheck = false,
}: Props) => {
    const { i18next } = useAppContext();
    const defaultProfile: TypedMap<ActiveProfile> = getDefaultProfile(i18next);
    const profile = activeProfile || defaultProfile;
    const contactName = extractName(contact);
    const statusExplanation = profile.get('operatorNote');
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const isAdmin = useAppSelector(selectIsAdmin);
    const canChangeProfile = useAppSelector((state) =>
        selectCanEditProfilesOfUser(state, extensionKey),
    );

    const canShowMenu =
        isAdmin ||
        canChangeProfile ||
        (extensionKey !== null && extensionKey === ownExtensionKey);

    return (
        <div className="flex max-w-full flex-1 flex-col break-words">
            <div className="text-2xl/10 @3xl:whitespace-nowrap">
                {title || contactName}
            </div>

            {canShowMenu && !skipAdminCheck ? (
                <ProfilePicker extensionKey={extensionKey} fixedWidth={true} />
            ) : (
                <CurrentProfile extensionKey={extensionKey} />
            )}

            {statusExplanation ? (
                <div className="mt-2 text-gray-500">{statusExplanation}</div>
            ) : null}
        </div>
    );
};

export default React.memo(ProfileItem);
